import React, { useEffect, useMemo, useState } from 'react';
import { Accordion } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { sharedTableStyles } from '../../utils/tableStyles';
import Select from 'react-select';
import DataTable from 'react-data-table-component';
import 'rc-calendar/assets/index.css';
import moment from 'moment';

import PageHeader from '../../components/common/PageHeader';
import DatePicker from '../../components/UI/DatePicker';

import { useGetActivityQuery } from '../../redux/apis/attendanceApi';
import { useIsLoggedInQuery } from '../../redux/apis/authApi';
import { useGetEmployeeDropDownQuery } from '../../redux/apis/employee';
import { selectDisplayTimezone } from '../../redux/utils/apiLocality';

import { HEADER_TITLE } from '../../utils/constant/header';
import { LB_ATTENDANCE_TABLE_COLS } from '../../utils/constant/labels';
import { skipRow } from '../../utils/handler';
import { CARD_USER_ATTENDANCE } from '../../utils/constant/card';
// import { notification } from '../../utils/notification';
import {
  aggregateDayTotals,
  buildActivityTimeline,
  buildDayTimelineSegments,
  buildTimedoctorRange,
  formatTimedoctorDate,
  formatTimedoctorTime,
  isWeekendDate,
  normalizeActivityWorkLogDays
} from '../../utils/timedoctorAttendanceHelpers';

import TimeDoctorActivityTimeline from './components/TimeDoctorActivityTimeline';
import './AttendanceTimeDoctor.scss';

const { LB_TBL_COL_START_TIME, LB_TBL_COL_END_TIME, LB_TBL_COL_DATE, LB_TBL_COL_TOTAL_HOURS } =
  LB_ATTENDANCE_TABLE_COLS;
const { TOTAL_TIME, TOTAL_BREAK } = CARD_USER_ATTENDANCE;
const { ATTENDANCE_EMPLOYEE } = HEADER_TITLE;

const initialState = {
  selectType: {
    type: 'Day',
    currentDate: moment()
  },
  dropdownDataStore: [],
  currentPageDropdown: 1,
  rowsPerPageDropdown: 50,
  timeDoctorUserId: null,
  selectedEmployeeLinked: null
};

const StatValue = ({ isLoading, hasUser, value }) => {
  if (!hasUser) return '—';
  if (isLoading) return <span className="td-att-stat-skeleton" aria-hidden />;
  return value || '0m';
};

const TableSkeleton = ({ rows = 5 }) => (
  <div className="att-tt-skeleton" aria-label="Loading">
    {Array.from({ length: rows }).map((_, index) => (
      <div key={`sk-${index}`} className="att-tt-skeleton__row">
        <div className="att-tt-skeleton__cell att-tt-skeleton__cell--expander" />
        <div className="att-tt-skeleton__cell att-tt-skeleton__cell--date" />
        <div className="att-tt-skeleton__cell att-tt-skeleton__cell--timeline" />
        <div className="att-tt-skeleton__cell att-tt-skeleton__cell--time" />
        <div className="att-tt-skeleton__cell att-tt-skeleton__cell--time" />
        <div className="att-tt-skeleton__cell att-tt-skeleton__cell--total" />
      </div>
    ))}
  </div>
);

const TimelineBar = ({ row, timezone }) => {
  const { segments } = buildDayTimelineSegments(row?.day, timezone);

  if (!segments.length) {
    return <span className="text-muted">—</span>;
  }

  return (
    <div className="att-tt-timeline" aria-label="Day timeline">
      <div className="att-tt-timeline__bar" aria-hidden>
        {segments.map((segment, index, arr) => {
          const isFirst = index === 0;
          const isLast = index === arr.length - 1;
          const roundClass =
            (isFirst ? ' att-tt-timeline__seg--start' : '') +
            (isLast ? ' att-tt-timeline__seg--end' : '');

          return (
            <div
              key={`seg-${index}`}
              className={`att-tt-timeline__seg${segment.kind === 'break' ? ' att-tt-timeline__seg--break' : ''}${roundClass}`}
              style={{
                left: `${segment.left * 100}%`,
                width: `${segment.width * 100}%`,
                background: segment.kind === 'break' ? undefined : segment.color
              }}
              title={segment.label}
            />
          );
        })}
      </div>
      <div className="att-tt-timeline__range" aria-hidden>
        <span>{formatTimedoctorTime(row?.day?.startTime, timezone)}</span>
        <span>{formatTimedoctorTime(row?.day?.endTime, timezone)}</span>
      </div>
    </div>
  );
};

// eslint-disable-next-line no-unused-vars
const ExpandedActivity = ({ data, timezone }) => (
  <div className="td-att-expand">
    <div className="td-att-expand__inner">
      <TimeDoctorActivityTimeline items={data.activityItems} variant="table" />
    </div>
  </div>
);

const WeekDateAccordion = ({ rows, timezone }) => {
  if (!rows.length) {
    return <div className="td-att-empty">No attendance for this period.</div>;
  }

  return (
    <div className="td-att-accordion-wrap">
      <Accordion defaultActiveKey={String(rows[0]?.id)}>
        {rows.map((row) => {
          const weekend = isWeekendDate(row.rawDate);

          return (
            <Accordion.Item
              key={row.id}
              eventKey={String(row.id)}
              className={`td-att-accordion-item${weekend ? ' td-att-accordion-item--weekend' : ''}`}
            >
              <Accordion.Header>
                <div className="td-att-acc-head">
                  <span
                    className={
                      weekend ? 'att-tt-date-badge att-tt-date-badge--weekend' : 'att-tt-date-badge'
                    }
                  >
                    {row.dateKey}
                  </span>

                  <div className="td-att-acc-head__timeline">
                    <TimelineBar row={row} timezone={timezone} />
                  </div>

                  <div className="td-att-acc-metrics">
                    <span className="td-att-acc-metric">
                      <span className="td-att-acc-metric__label">Start</span>
                      <b>{row.startTime}</b>
                    </span>
                    <span className="td-att-acc-metric">
                      <span className="td-att-acc-metric__label">End</span>
                      <b>{row.endTime}</b>
                    </span>
                    <span className="td-att-acc-metric">
                      <span className="td-att-acc-metric__label">Total</span>
                      <b>{row.totalFormatted}</b>
                    </span>
                  </div>
                </div>
              </Accordion.Header>

              <Accordion.Body>
                {row.hasActivity ? (
                  <ExpandedActivity data={row} timezone={timezone} />
                ) : (
                  <div className="td-att-empty">No activity for this day.</div>
                )}
              </Accordion.Body>
            </Accordion.Item>
          );
        })}
      </Accordion>
    </div>
  );
};

function AttendanceEmployees() {
  const timeZone = useSelector(selectDisplayTimezone) || 'Asia/Kolkata';
  const initialRange = buildTimedoctorRange(moment(), timeZone, 'Day');

  const [state, setState] = useState(initialState);
  const [fromDate, setFromDate] = useState(initialRange.from);
  const [toDate, setToDate] = useState(initialRange.to);

  const { data } = useIsLoggedInQuery();
  const values = data?.data;
  const isAdmin = values?.hrxRole?.roleName === 'Admin';
  const timeDoctorUserId = isAdmin ? state?.timeDoctorUserId : values?.timeDoctorUserId;

  const { data: dropdownData, refetch: employeeDropDownQuery } = useGetEmployeeDropDownQuery(
    {
      skip: skipRow(state.currentPageDropdown, state.rowsPerPageDropdown),
      limit: state.rowsPerPageDropdown,
      isTimedoctorUser: true
    },
    { skip: !isAdmin }
  );

  // const [syncTimeDoctorUserIds, { isLoading: isSyncing }] = useSyncTimeDoctorUserIdsMutation();

  const {
    data: activityData,
    isLoading,
    isFetching
  } = useGetActivityQuery(
    {
      user: timeDoctorUserId,
      timezone: timeZone,
      from: fromDate,
      to: toDate
    },
    { skip: !timeDoctorUserId, refetchOnMountOrArgChange: true }
  );

  const attendanceDays = useMemo(
    () => normalizeActivityWorkLogDays(activityData?.data, timeZone),
    [activityData?.data, timeZone]
  );

  const summaryTotals = useMemo(() => {
    if (state.selectType.type === 'Day' && attendanceDays[0]) {
      const day = attendanceDays[0];
      return {
        totalTrackedFormatted: day.totalTrackedFormatted || '0m',
        totalBreakFormatted: day.totalBreakFormatted || '0m'
      };
    }
    return aggregateDayTotals(attendanceDays);
  }, [attendanceDays, state.selectType.type]);

  const tableData = useMemo(
    () =>
      attendanceDays
        .filter((day) => day?.startTime && (day?.sessions?.length || 0) > 0)
        .map((day) => ({
          id: day?.date || `day-${day?.startTime}`,
          dateKey: formatTimedoctorDate(day?.date, timeZone),
          rawDate: day?.date,
          startTime: formatTimedoctorTime(day?.startTime, timeZone),
          endTime: formatTimedoctorTime(day?.endTime, timeZone),
          totalFormatted: day?.totalTrackedFormatted || '0m',
          day,
          activityItems: buildActivityTimeline([day], timeZone),
          hasActivity: (day?.sessions?.length || 0) + (day?.breaks?.length || 0) > 0
        })),
    [attendanceDays, timeZone]
  );

  const isPending = isLoading || isFetching;
  const isMultiDayView = state.selectType.type === 'Week' || state.selectType.type === 'Month';

  const applyDateRange = (value, rangeType) => {
    const { from, to } = buildTimedoctorRange(value, timeZone, rangeType);
    setFromDate(from);
    setToDate(to);
  };

  // Recalc API from/to when office (timezone) changes
  useEffect(() => {
    const value = state.selectType?.currentDate || moment();
    const type = state.selectType?.type || 'Day';
    const { from, to } = buildTimedoctorRange(value, timeZone, type);
    setFromDate(from);
    setToDate(to);
  }, [timeZone, state.selectType?.currentDate, state.selectType?.type]);

  const handleDateSelection = (type) => {
    const value = state.selectType?.currentDate || moment();
    setState((prev) => ({
      ...prev,
      selectType: { type, currentDate: value }
    }));
    applyDateRange(value, type);
  };

  const handleDateSelectionRange = (value) => {
    const next = value || moment();
    setState((prev) => ({
      ...prev,
      selectType: { ...prev.selectType, currentDate: next }
    }));
    applyDateRange(next, state.selectType.type);
  };

  const handleMenuScrollToBottom = async () => {
    if (dropdownData?.isNext) {
      setState((prevState) => ({
        ...prevState,
        currentPageDropdown: prevState.currentPageDropdown + 1
      }));
      employeeDropDownQuery();
    }
  };

  // const handleSyncTimeDoctorUsers = async () => {
  //   try {
  //     const result = await syncTimeDoctorUserIds().unwrap();
  //     const summary = result?.data || result;
  //     notification(
  //       `TimeDoctor sync done — updated ${summary?.updated ?? 0}, matched ${summary?.matched ?? 0}`,
  //       'success'
  //     );

  //     setState((prev) => ({
  //       ...prev,
  //       currentPageDropdown: 1,
  //       timeDoctorUserId: null,
  //       selectedEmployeeLinked: null
  //     }));
  //     await employeeDropDownQuery();
  //   } catch (error) {
  //     notification(error?.data?.message || error?.message || 'TimeDoctor sync failed', 'error');
  //   }
  // };

  useEffect(() => {
    if (dropdownData && Array.isArray(dropdownData.data)) {
      setState((prevState) => {
        const isFirstPage = prevState.currentPageDropdown === 1;
        return {
          ...prevState,
          dropdownDataStore: isFirstPage
            ? dropdownData.data
            : [...prevState.dropdownDataStore, ...dropdownData.data]
        };
      });
    }
  }, [dropdownData]);

  const tableColumns = [
    {
      name: LB_TBL_COL_DATE,
      selector: (row) => row.dateKey,
      sortable: true,
      width: '140px',
      cell: (row) => (
        <span
          className={
            isWeekendDate(row.rawDate)
              ? 'att-tt-date-badge att-tt-date-badge--weekend'
              : 'att-tt-date-badge'
          }
        >
          {row.dateKey}
        </span>
      )
    },
    {
      name: 'TIMELINE',
      sortable: false,
      width: '260px',
      cell: (row) => <TimelineBar row={row} timezone={timeZone} />
    },
    {
      name: LB_TBL_COL_START_TIME,
      selector: (row) => row.startTime,
      width: '120px'
    },
    {
      name: LB_TBL_COL_END_TIME,
      selector: (row) => row.endTime,
      width: '120px'
    },
    {
      name: LB_TBL_COL_TOTAL_HOURS,
      selector: (row) => row.totalFormatted,
      width: '120px'
    }
  ];

  return (
    <div className="container-xxl att-tt-page td-att-page">
      <div className="att-tt-page__title">
        <PageHeader headerTitle={ATTENDANCE_EMPLOYEE} />
      </div>

      <div className="att-tt-body">
        <div className="att-tt-surface">
          <div className="att-tt-surface__toolbar td-att-toolbar">
            <div className="td-att-toolbar__inner">
              <div className="td-att-toolbar__stats" aria-label="Attendance summary">
                <div className="att-tt-stat td-att-stat--tracked">
                  <span className="att-tt-stat__icon" aria-hidden>
                    <i className="bi bi-clock-history" />
                  </span>
                  <div className="att-tt-stat__content">
                    <span className="att-tt-stat__label">{TOTAL_TIME}</span>
                    <span className="att-tt-stat__value">
                      <StatValue
                        isLoading={isPending}
                        hasUser={Boolean(timeDoctorUserId)}
                        value={summaryTotals.totalTrackedFormatted}
                      />
                    </span>
                  </div>
                </div>

                <div className="att-tt-stat td-att-stat--break">
                  <span className="att-tt-stat__icon" aria-hidden>
                    <i className="bi bi-cup-hot" />
                  </span>
                  <div className="att-tt-stat__content">
                    <span className="att-tt-stat__label">{TOTAL_BREAK}</span>
                    <span className="att-tt-stat__value">
                      <StatValue
                        isLoading={isPending}
                        hasUser={Boolean(timeDoctorUserId)}
                        value={summaryTotals.totalBreakFormatted}
                      />
                    </span>
                  </div>
                </div>
              </div>

              <div className="td-att-toolbar__divider" aria-hidden />

              <div className="td-att-toolbar__filters">
                {isAdmin && (
                  <div className="att-tt-control att-tt-control--employee td-att-toolbar__employee">
                    <span className="att-tt-control__icon" aria-hidden>
                      <i className="bi bi-person" />
                    </span>
                    <Select
                      classNamePrefix="att-tt-select"
                      className="att-tt-select-wrap"
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      options={state.dropdownDataStore?.map((val) => ({
                        value: val._id,
                        label: val.name
                      }))}
                      onMenuScrollToBottom={handleMenuScrollToBottom}
                      placeholder="Select Employee..."
                      onChange={(selectedOption) => {
                        const selectedEmployee = state.dropdownDataStore.find(
                          (employee) => employee._id === selectedOption.value
                        );
                        if (selectedEmployee) {
                          setState((prev) => ({
                            ...prev,
                            timeDoctorUserId: selectedEmployee.timeDoctorUserId || null,
                            selectedEmployeeLinked: Boolean(selectedEmployee.timeDoctorUserId)
                          }));
                        }
                      }}
                    />
                  </div>
                )}

                <div className="td-att-toolbar__period">
                  <div className="att-tt-tabs" role="tablist" aria-label="Time period">
                    {['Day', 'Week', 'Month'].map((period) => (
                      <button
                        key={period}
                        type="button"
                        role="tab"
                        aria-selected={state.selectType.type === period}
                        className={`att-tt-tab${state.selectType.type === period ? ' att-tt-tab--active' : ''}`}
                        onClick={() => handleDateSelection(period)}
                      >
                        {period}
                      </button>
                    ))}
                  </div>

                  <div
                    className={`att-tt-date att-tt-date--${state.selectType.type.toLowerCase()} td-att-toolbar__date`}
                  >
                    <span className="att-tt-date__icon" aria-hidden>
                      <i className="bi bi-calendar3" />
                    </span>
                    <div className="att-tt-date__picker">
                      <DatePicker
                        initialDate={state.selectType.currentDate}
                        type={state.selectType.type}
                        handleChangeDate={handleDateSelectionRange}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="att-tt-surface__body">
            {!timeDoctorUserId ? (
              <div className="td-att-empty m-3">
                {!isAdmin
                  ? 'TimeDoctor user not linked.'
                  : state.selectedEmployeeLinked === false
                    ? 'Selected employee is not linked to TimeDoctor.'
                    : 'Select an employee to view TimeDoctor attendance.'}
              </div>
            ) : isPending ? (
              <TableSkeleton
                rows={isMultiDayView ? (state.selectType.type === 'Month' ? 12 : 7) : 3}
              />
            ) : isMultiDayView ? (
              <WeekDateAccordion rows={tableData} timezone={timeZone} />
            ) : (
              <div className="att-tt-table">
                <DataTable
                  columns={tableColumns}
                  data={tableData}
                  keyField="id"
                  expandableRows
                  expandableRowDisabled={(row) => !row.hasActivity}
                  expandableRowsComponent={(props) => (
                    <ExpandedActivity {...props} timezone={timeZone} />
                  )}
                  highlightOnHover
                  pagination={false}
                  selectableRows={false}
                  className="table myDataTable table-hover align-middle mb-0 d-row nowrap dataTable no-footer dtr-inline"
                  customStyles={sharedTableStyles}
                  noDataComponent={
                    <div className="td-att-empty">No attendance for this period.</div>
                  }
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AttendanceEmployees;
